import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import type { FaceDetectResult } from '../types';

const SCAN_INTERVAL_MS = 200;

type UseLivenessDetectLoopParams = {
    isDone: MutableRefObject<boolean>;
    isPaused: MutableRefObject<boolean>;
    isDetectInProgress: MutableRefObject<boolean>;
    faceDetectTimeout: MutableRefObject<ReturnType<typeof setTimeout> | null>;
    detect: () => Promise<FaceDetectResult | undefined>;
    checkFaceDetectedResult: (res: FaceDetectResult) => Promise<void>;
    errorProceedText?: string;
};

export const useLivenessDetectLoop = ({
    isDone,
    isPaused,
    isDetectInProgress,
    faceDetectTimeout,
    detect,
    checkFaceDetectedResult,
    errorProceedText,
}: UseLivenessDetectLoopParams) => {
    const detectGenerationRef = useRef(0);
    const startDetectingRef = useRef<() => void>(() => {});

    const abortDetectLoop = useCallback(() => {
        detectGenerationRef.current += 1;
        isDetectInProgress.current = false;

        if (faceDetectTimeout.current) {
            clearTimeout(faceDetectTimeout.current);
            faceDetectTimeout.current = null;
        }
    }, [faceDetectTimeout, isDetectInProgress]);

    const startDetecting = useCallback(async () => {
        const generation = detectGenerationRef.current;

        if (isDone.current || errorProceedText) {
            return;
        }

        if (!isPaused.current && !isDetectInProgress.current) {
            isDetectInProgress.current = true;

            try {
                const res = await detect();

                if (detectGenerationRef.current !== generation) {
                    return;
                }

                if (res) {
                    await checkFaceDetectedResult(res);
                }
            } finally {
                if (detectGenerationRef.current === generation) {
                    isDetectInProgress.current = false;
                }
            }
        }

        if (detectGenerationRef.current !== generation || isDone.current) {
            return;
        }

        faceDetectTimeout.current = setTimeout(() => {
            startDetectingRef.current();
        }, SCAN_INTERVAL_MS);
    }, [
        checkFaceDetectedResult,
        detect,
        faceDetectTimeout,
        isDetectInProgress,
        isDone,
        isPaused,
        errorProceedText,
    ]);

    useEffect(() => {
        startDetectingRef.current = () => {
            startDetecting().catch(() => {});
        };
    }, [startDetecting]);

    return { startDetecting, startDetectingRef, abortDetectLoop };
};
